import { HttpClient } from '@angular/common/http';
import { Component, OnInit } from '@angular/core';
import { lastValueFrom } from 'rxjs';
import { AppConstants } from 'src/app/shared/constants/app.contants';
import { ApiService } from 'src/app/shared/services/api.service';
import { LoaderService } from 'src/app/shared/services/loader.service';
import { environment } from 'src/environments/environment';
import { ToastService } from 'src/app/shared/services/toast.service';
import { VendorDto } from 'src/app/modules/authentication/components/models/autentication-dto.models';

@Component({
  selector: 'app-manage-vendors',
  templateUrl: './manage-vendors.component.html',
  styleUrls: ['./manage-vendors.component.scss']
})
export class ManageVendorsComponent implements OnInit {
  vendors: any[] = [];

  constructor(
    private _http: HttpClient,
    private _loaderService: LoaderService,
    private _toastService: ToastService
  ) {

  }

  ngOnInit(): void {
    console.log('ngOnInit');

    this.loadVendors();
    
  }

  private createApiService(): ApiService {
    const apiService = new ApiService(this._http);
    apiService['_baseUrl'] = environment.API_URL;
    return apiService;
  }

  async loadVendors(): Promise<void> {
    try {
      this.vendors = await this.getVendors();
      this._toastService.success('Vendors loaded successfully!', 'Success');
    } catch (error) {
      this._toastService.error('Failed to load vendors.', 'Error');
      console.error('Error loading vendors:', error);
    }
  }

  async getVendors(): Promise<any[]> {
    this._loaderService.showLoader();
    const apiService = this.createApiService();
    const req$ = apiService.get(
      AppConstants.CONTROLLERS.VENDOR,
      AppConstants.ACTIONS.GetAllVendorsInfo,
      true,
      environment.APIs_PREFIX
    );

    try {
      const data: any = await lastValueFrom(req$);

    
      const mappedData = data?.data?.map((item: any): VendorDto => ({
        email: item.email,
        ERPVendorID: item.erP_VendorID,
        mobile: item.mobile,
        vendorID: item.vendorID,
        status: item.status,
        id: item.id,
        createdBy: item.createdBy,
        vendorReference: item.vendorReference
      })) || [];
      return mappedData;
    } catch (err: any) {
      throw err;
    } finally {
      this._loaderService.hideLoader();
    }
  }

  async deleteVendor(vendor: VendorDto): Promise<void> {
    if (!confirm(`Are you sure you want to delete vendor ${vendor.vendorID}?`)) {
      return;
    }

    this._loaderService.showLoader();
    const apiService = this.createApiService();
    
    try {
      const response = await this.fireDeleteVendor(apiService, vendor.vendorID);
      if (response?.isSuccess) {
        this._toastService.success('Vendor deleted successfully!', 'Success');
        // Refresh the vendors list
        await this.loadVendors();
      } else {
        this._toastService.error(response?.errorMessageEn || 'Failed to delete vendor.', 'Error');
      }
    } catch (error) {
      console.error('Failed to delete vendor:', error);
      this._toastService.error('Failed to delete vendor due to an unexpected error.', 'Error');
    } finally {
      this._loaderService.hideLoader();
    }
  }

  private fireDeleteVendor(apiService: ApiService, vendorId: string): Promise<any> {
    const req$ = apiService.delete(
      AppConstants.CONTROLLERS.VENDOR,
      `${AppConstants.ACTIONS.DeleteVendor}/${vendorId}`,
      true,
      environment.APIs_PREFIX
    );
    return lastValueFrom(req$);
  }

  async inviteVendor(vendor: VendorDto): Promise<void> {
    // Extra guard on frontend even though backend also enforces this rule
    if (vendor.createdBy !== 'ERP') {
      return;
    }

    this._loaderService.showLoader();
    const apiService = this.createApiService();

    const payload = {
      vendorId: vendor.vendorID
    };

    const req$ = apiService.post(
      AppConstants.CONTROLLERS.VENDOR,
      AppConstants.ACTIONS.InviteVendor,
      true,
      environment.APIs_PREFIX,
      payload
    );

    try {
      const response: any = await lastValueFrom(req$);

      if (response?.isSuccess) {
        this._toastService.success('Vendor invited successfully!', 'Success');
        await this.loadVendors();
      } else {
        this._toastService.error(response?.errorMessageEn || 'Failed to invite vendor.', 'Error');
      }
    } catch (error) {
      console.error('Failed to invite vendor:', error);
      this._toastService.error('Failed to invite vendor due to an unexpected error.', 'Error');
    } finally {
      this._loaderService.hideLoader();
    }
  }

  async migrateVendor(vendor: VendorDto): Promise<void> {
    if (!confirm(`Are you sure you want to migrate vendor ${vendor.vendorID} to ERP?`)) {
      return;
    }

    this._loaderService.showLoader();
    const apiService = this.createApiService();
    
    try {
      const response = await this.fireMigrateVendor(apiService, vendor.vendorID);
      if (response?.isSuccess) {
        this._toastService.success('Vendor migrated to ERP successfully!', 'Success');
        // Refresh the vendors list
        await this.loadVendors();
      } else {
        this._toastService.error(response?.errorMessageEn || 'Failed to migrate vendor to ERP.', 'Error');
      }
    } catch (error) {
      console.error('Failed to migrate vendor to ERP:', error);
      this._toastService.error('Failed to migrate vendor to ERP due to an unexpected error.', 'Error');
    } finally {
      this._loaderService.hideLoader();
    }
  }

  private fireMigrateVendor(apiService: ApiService, vendorId: string): Promise<any> {
    console.log('Vendor ID being sent:', vendorId);
    
    // The API expects vendorId as a query parameter, not in the body
    const keyValueParams = { vendorId: vendorId };
    
    const req$ = apiService.post(
      AppConstants.CONTROLLERS.Common,
      AppConstants.ACTIONS.MigrateVendorToERP,
      true,
      environment.APIs_PREFIX,
      null, // No payload body needed
      keyValueParams
    );
    return lastValueFrom(req$);
  }
} 